import { useState, useEffect, useContext, useRef } from "react";
import { services } from "../services/api";
import { useCsrfSession } from "../shared/hooks/useCsrfSession";
import { Link, useNavigate } from "react-router-dom";
import { GlobalContext } from "../context/global_context";
import InputLabel from "@mui/material/InputLabel";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import { NotificacionContext } from "../context/notificacion_context";

const InicioSesion = ({ login, setLogin }) => {
  const navigate = useNavigate();
  const { ensureReady: ensureCsrfReady } = useCsrfSession();
  
  useEffect(() => {
    const loadData = async () => {
      const match = document.cookie.match(/(?:^|;\s*)app_auth_token=([^;]*)/);
      setLogin(match ? match[1] : null);
      const timeoutId = setTimeout(() => {
        setState((prevState) => ({
          ...prevState,
          loading: false,
        }));
      }, 1500);
      return () => clearTimeout(timeoutId);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const loginAltchaRef = useRef(null);
  const submittingRef = useRef(false);
  const [altchaReady, setAltchaReady] = useState(Boolean(customElements.get("altcha-widget")));
  const challengeUrl = process.env.NODE_ENV === "development" || !process.env.REACT_APP_URLAPI
    ? "/api/altcha-challenge"
    : `${process.env.REACT_APP_URLAPI}altcha-challenge`;

  useEffect(() => {
    let active = true;
    customElements.whenDefined("altcha-widget").then(() => {
      if (active) setAltchaReady(true);
    });
    return () => { active = false; };
  }, []);

  const [state, setState] = useState({
    email: "",
    pass: "",
    error: "",
    showPassword: false,
    inscribiendo: false,
    loading: true,
  });

  const { GlobalState, setGlobalState } = useContext(GlobalContext);
  const { showNotification } = useContext(NotificacionContext);

  const handdleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    const email = state.email;
    const pass = state.pass;

    if (email === "") {
      showNotification(
        "warning",
        "Todos los campos son requeridos",
        "Ingresa un Email",
        null,
        4
      );
    } else if (pass === "") {
      showNotification(
        "warning",
        "Todos los campos son requeridos",
        "Ingresa una Contraseña",
        null,
        4
      );
    } else {
      const widget = loginAltchaRef.current;
      if (!widget || typeof widget.reset !== "function" || typeof widget.verify !== "function") {
        showNotification("error", "El captcha no está listo, por favor espera un momento", null, null, 4);
        return;
      }
      submittingRef.current = true;
      setState((prev) => ({ ...prev, inscribiendo: true }));

      try {
        try {
          await ensureCsrfReady();
        } catch {
          showNotification("error", "No se pudo preparar la sesión. Intenta de nuevo.", null, null, 4);
          return;
        }
        let altchaPayload;
        try {
          widget.reset();
          const result = await widget.verify();
          if (typeof result?.payload !== "string" || !result.payload.trim()) {
            throw new Error("No payload returned from verify()");
          }
          altchaPayload = result.payload;
        } catch {
          showNotification("error", "Error al verificar el captcha", null, null, 4);
          return;
        }

        const payloadWithCaptcha = new FormData();
        payloadWithCaptcha.append("email", email);
        payloadWithCaptcha.append("password", pass);
        payloadWithCaptcha.append("altcha", altchaPayload);

        const dataApiUser = await services(
          "POST",
          "login",
          payloadWithCaptcha
        );

        if (dataApiUser && dataApiUser.status === 200 && dataApiUser.data && !dataApiUser.data.error) {
          const receivedToken = dataApiUser.data.token || dataApiUser.data.access_token || "auth-session";
          document.cookie = `app_auth_token=${receivedToken}; path=/; max-age=86400;`;
          setLogin(receivedToken);

          if (dataApiUser.data.curp) await localStorage.setItem("curp", dataApiUser.data.curp);
          if (dataApiUser.data.id) await localStorage.setItem("id", dataApiUser.data.id);
          if (dataApiUser.data.nombre) await localStorage.setItem("nombre", dataApiUser.data.nombre);
          if (dataApiUser.data.rol) localStorage.setItem("rol", dataApiUser.data.rol);

          if (dataApiUser.data.rol === "Admin") {
            navigate("/admin");
          } else if (dataApiUser.data.rol === "Profesor") {
            navigate("/mis-talleres");
          } else if (dataApiUser.data.rol === "Alumno") {
            navigate("/mis-cursos");
          } else {
            if (localStorage.getItem("tallerSelected")) {
              await inscribir(localStorage.getItem("tallerSelected"));
            } else {
              navigate("/");
            }
          }
        } else {
          showNotification(
            "warning",
            "No se pudo iniciar sesión",
            dataApiUser?.data?.mensaje || dataApiUser?.data?.error || "Revisa tus credenciales o conexión",
            null,
            4
          );
        }
      } catch {
        showNotification("error", "No se pudo iniciar sesión", "Revisa tus credenciales o conexión", null, 4);
      } finally {
        submittingRef.current = false;
        setState((prev) => ({ ...prev, inscribiendo: false }));
      }
    }
  };

  const handlePassword = (e) => {
    setState({ ...state, pass: e.target.value });
  };

  const handleEmail = (e) => {
    setState({ ...state, email: e.target.value });
  };

  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };

  const inscribir = async (data) => {
    const dataIscripcion = await services(
      "GET",
      `usuarios/crearinscripcionalumno?taller=${data}`,
      null
    );
    if (dataIscripcion.status === 200) {
      if (dataIscripcion.data?.error) {
        setState({ ...state, inscribiendo: false });
        showNotification(
          "warning",
          "Aviso",
          dataIscripcion.data.error,
          null,
          4
        );
      } else {
        setGlobalState({
          ...GlobalState,
          lineaCaptura: dataIscripcion.data,
          openpagoLinea: true,
        });
        setState({ ...state, inscribiendo: false });
        navigate("/mis-cursos");
      }
    } else {
      setState({ ...state, inscribiendo: false });
      showNotification(
        "error",
        "Error",
        "Ocurrió un error al inscribir",
        null,
        4
      );
    }
  };

  return (
    <>
      {altchaReady && (
        <altcha-widget
          ref={loginAltchaRef}
          challenge={challengeUrl}
          language="es-419"
          hidebutton="true"
          auto="off"
          style={{ display: 'none' }}
        ></altcha-widget>
      )}
      {state.loading ? (
        <div style={{ height: "calc(100vh - 80px)" }}>
          <span className="loader"></span>
        </div>
      ) : (
        <div className="bg-fondo">
            <div className="container " style={{ height: "calc(100vh - 60px)", marginTop: "-2rem" }}>
              <div className="d-flex align-items-center justify-content-center h-100">
                <form onSubmit={handdleSubmit} noValidate>
                  <div className="card">
                    <div className="my-3 text--morado text-bold borderBottomTitulo">
                      Inicia Sesión
                    </div>

                    <FormControl sx={{ width: "100%" }} variant="standard">
                      <InputLabel htmlFor="standard-adornment-password">
                        Email
                      </InputLabel>
                      <Input
                        onChange={handleEmail}
                        variant="outlined"
                        id="standard-adornment-email"
                        type={"text"}
                      />
                    </FormControl>

                    <FormControl
                      sx={{ marginTop: 2, width: "100%" }}
                      variant="standard"
                    >
                      <InputLabel htmlFor="standard-adornment-password">
                        Password
                      </InputLabel>
                      <Input
                        variant="outlined"
                        onChange={handlePassword}
                        id="standard-adornment-password"
                        type={state.showPassword ? "text" : "password"}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                            >
                              {state.showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    </FormControl>
                    <div className="mt-3">
                      <button className="cta cta--guinda" type="submit" disabled={state.inscribiendo}>
                        {state.inscribiendo ? "Verificando…" : "Entrar"}
                      </button>
                      <div
                        className="cta cta--guinda mt-2  "
                        onClick={() => navigate("/crearCuenta")}
                      >
                        Crear Cuenta
                      </div>
                      <p style={{ marginTop: "5%" }}>
                        ¿Olvidaste tu contraseña? click{" "}
                        <Link to={"/reset/password"}>aqui</Link>
                      </p>
                    </div>

                    <div
                      className="text-center mt-3"
                      style={{ height: "30px", color: "red" }}
                    >
                      {state.error}
                    </div>

                    <div></div>
                  </div>
                </form>
              </div>
            </div>
        </div>
      )}
    </>
  );
};

export default InicioSesion;
